import { computed, makeObservable, observable, ObservableMap, reaction } from "mobx";
import { throttle } from "lodash-es";
import { uuidModule } from "@/modules/uuid";
import { NavigationPath, Pane, Tab, TabChangeHandler } from "./types";
import { NavigateFunction, To, NavigateOptions, Path, matchPath } from "react-router";
import { logger } from "@/modules/logger";
import { clientEnvModule } from "@/modules/client-env";
import { AppRoute } from "@/app/router/utils";
import { action } from "mobx";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import {
  convertPathToString,
  stripTidFromPath,
  convertPathToStringWithoutTid,
} from "@/store/navigation/helpers";
import { appRoutes } from "@/app/router";
import { Uuid } from "@/domains/global/identifiers";

export interface NavigationTabManagerOptions {
  navigate: NavigateFunction | null;
  onTabChange: TabChangeHandler;
}

export interface NavigationConfig {
  routerOptions?: NavigateOptions;
  resetStack?: boolean;
  /** If true, opens without switching focus */
  openInBackground?: boolean;
  /** If true, suppresses onTabChange emission for this navigation */
  disableTabChangeEvent?: boolean;
  /** If true, downstream listeners should suppress side panel tab switching */
  suppressSidePanelRouteHandler?: boolean;
}

export enum TabOpeningStrategy {
  SAME_TAB = "SAME_TAB",
  NEW_TAB = "NEW_TAB",
  INACTIVE_PANE = "INACTIVE_PANE",
  NEW_TAB_BACKGROUND = "NEW_TAB_BACKGROUND",
  INACTIVE_PANE_BACKGROUND = "INACTIVE_PANE_BACKGROUND",
}

export interface NavigationState {
  panesMap: ObservableMap<string, Pane>;
  tabsMap: ObservableMap<string, Tab>;
  activePaneId: string;
  activeTabId: string;
  lastActivePaneId: string | null;
}

export interface SerializedNavigationState {
  panes: Record<string, Pane>;
  tabs: Record<string, Tab>;
  activePaneId: string;
  activeTabId: string;
  lastActivePaneId: string | null;
}

export class NavigationTabManager {
  readonly MAX_PANES: number = 2;
  readonly NEW_TAB_PATH = "/notes";
  readonly SESSION_STORAGE_KEY = "navigation-tab-manager-state";
  readonly MIN_PANE_WIDTH = 400;
  readonly MIN_CONTAINER_WIDTH = this.MIN_PANE_WIDTH * this.MAX_PANES;

  navigate: NavigateFunction | null;
  onTabChange: TabChangeHandler;
  panesMap = new ObservableMap<string, Pane>();
  tabsMap = new ObservableMap<string, Tab>();
  activePaneId: string;
  activeTabId: string;
  lastActivePaneId: string | null = null;
  desktopStartupUrlChangeCount: number = 0;

  constructor({ navigate, onTabChange }: NavigationTabManagerOptions) {
    this.navigate = navigate;
    this.onTabChange = onTabChange;

    // Restore from session storage
    const state = this._restoreFromSessionStorage();
    if (state) {
      this.panesMap = state.panesMap;
      this.tabsMap = state.tabsMap;
      this.activePaneId = state.activePaneId;
      this.activeTabId = state.activeTabId;
      this.lastActivePaneId = state.lastActivePaneId;
    } else {
      const defaultState = this._initializeDefaultPane();
      this.panesMap = defaultState.panesMap;
      this.tabsMap = defaultState.tabsMap;
      this.activePaneId = defaultState.activePaneId;
      this.activeTabId = defaultState.activeTabId;
      this.lastActivePaneId = defaultState.lastActivePaneId;
    }
    this._createInitialStateFromUrl();

    makeObservable(this, {
      // ===== OBSERVABLE STATE =====
      panesMap: observable,
      tabsMap: observable,
      activePaneId: observable,
      activeTabId: observable,
      lastActivePaneId: observable,
      panes: computed,
      allTabsFlattened: computed,
      activeNoteIds: computed,
      desktopStartupUrlChangeCount: observable,
      hasDesktopLoaded: computed,

      // ===== PUBLIC READ-ONLY HELPERS =====
      getTabsForPane: false,
      getResponsivePanes: false,

      // ===== PUBLIC MUTATIONS / NAVIGATION =====
      openTab: action,
      openInSameTab: action,
      openInNewTab: action,
      openInInactivePane: action,
      setPaneFocused: action,
      setActivePane: action,
      createPane: action,
      splitPaneWithTab: action,
      moveTabToPane: action,
      switchToTab: action,
      closeTab: action,
      closePane: action,
      _makeSureActivePaneIsValid: action,
      handleUrlChange: action,
      clearTabState: action,

      // ===== INTERNAL HELPERS =====
      _getTab: false,
      _getPane: false,
      _deleteTab: action,
      _updateTab: action,
      _reorderTabs: true,
      _reorderTabsAfterInsert: true,
      _reorderPanes: true,
      _moveTabToDifferentPane: true,
      _insertNewTab: action,
      _insertNewPane: action,
      _deletePane: true,
      _navigateToTab: true,
      _getNextTabOrder: true,
      _getNextPaneOrder: true,
      _convertPathToToWithTid: false,
      _getCurrentUrl: true,
      _setPaneActiveTabWithoutFocus: true,
      _calcNextTabToActivate: true,

      // ===== INITIALIZATION & PERSISTENCE =====
      _initializeDefaultPane: action,
      _createInitialStateFromUrl: action,
      _serializeState: false,
      _deserializeState: false,
      _saveToSessionStorage: action,
      _restoreFromSessionStorage: action,
      _clearSessionStorage: action,
      _attachPersistenceReaction: false,
      _validateNavigationState: false,

      // ===== CONSTANTS / EXTERNAL =====
      navigate: false,
      onTabChange: false,
      setNavigateFunction: action,
      clearPersistedState: action,

      NEW_TAB_PATH: false,
      MAX_PANES: false,
      SESSION_STORAGE_KEY: false,
      MIN_PANE_WIDTH: false,
      MIN_CONTAINER_WIDTH: false,
    });

    this._attachPersistenceReaction();
  }

  // ===== COMPUTED PROPERTIES =====

  /**
   * Gets all panes sorted by order
   * @returns Array of panes sorted by order
   */
  get panes(): Pane[] {
    return [...this.panesMap.values()].sort((a, b) => a.order - b.order);
  }

  /**
   * Gets all non-default tabs from all panes (used for overflow display)
   */
  get allTabsFlattened(): Tab[] {
    return this.panes.flatMap(pane => {
      const paneTabs = this.getTabsForPane(pane.id);
      return paneTabs.filter(tab => !tab.isDefault);
    });
  }

  /**
   * Gets the IDs of the active notes
   * @returns Set of active note IDs
   */
  get activeNoteIds(): Set<Uuid> {
    const activeNoteIds = new Set<Uuid>();
    this.tabsMap.forEach(tab => {
      const noteIdMatch = matchPath(
        appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
        tab.path
      );
      if (noteIdMatch?.params.noteId) activeNoteIds.add(noteIdMatch.params.noteId);
    });
    return activeNoteIds;
  }

  get hasDesktopLoaded(): boolean {
    // Desktop runs mainWindow.loadURL to /notes on launch
    // useEffect causes handleUrlChange to run twice
    return clientEnvModule.isDesktop() && this.desktopStartupUrlChangeCount >= 2;
  }

  /**
   * Gets tabs for a specific pane
   * @param paneId - The id of the pane to get tabs for
   * @returns Array of tabs for the pane sorted by order
   */
  getTabsForPane(paneId: string): Tab[] {
    const tabs: Tab[] = [];
    this.tabsMap.forEach(tab => {
      if (tab.paneId === paneId) tabs.push(tab);
    });
    return tabs.sort((a, b) => a.order - b.order);
  }

  /**
   * Gets visible and hidden panes based on container width
   * @param containerWidth - The current container width
   * @returns Object with visiblePanes and hiddenPanes arrays
   */
  getResponsivePanes(containerWidth: number): {
    visiblePanes: Pane[];
    hiddenPanes: Pane[];
    shouldHidePanes: boolean;
  } {
    if (containerWidth === 0 || containerWidth >= this.MIN_CONTAINER_WIDTH) {
      return {
        visiblePanes: this.panes,
        hiddenPanes: [],
        shouldHidePanes: false,
      };
    }

    const activePane = this.panesMap.get(this.activePaneId);
    const visiblePanes = activePane ? [activePane] : [];
    const hiddenPanes = this.panes.filter(pane => pane.id !== activePane?.id);

    return {
      visiblePanes,
      hiddenPanes,
      shouldHidePanes: true,
    };
  }

  // ===== HELPER METHODS =====

  /**
   * Gets a tab
   * @param tabId - The id of the tab to get
   * @returns The tab, or null if it does not exist
   */
  _getTab(tabId: string): Tab | null {
    const tab = this.tabsMap.get(tabId);
    if (!tab) {
      logger.warn({ message: `[NavigationTabManager][_getTab] Tab ${tabId} not found` });
      return null;
    }
    return tab;
  }

  /**
   * Gets a pane
   * @param paneId - The id of the pane to get
   * @returns The pane, or null if it does not exist
   */
  _getPane(paneId: string): Pane | null {
    const pane = this.panesMap.get(paneId);
    if (!pane) {
      logger.warn({ message: `[NavigationTabManager][_getPane] Pane ${paneId} not found` });
      return null;
    }
    return pane;
  }

  /**
   * Inserts a new tab
   * @param paneId - The id of the pane to insert the tab into
   * @param path - The path of the tab
   * @param order - The order of the tab
   * @param tabId - The id of the tab
   * @returns The new tab
   */
  _insertNewTab({
    paneId,
    path,
    order,
    tabId,
    isDefault,
  }: {
    paneId: string;
    path: string;
    order: number;
    tabId?: string;
    isDefault?: boolean;
  }): Tab {
    const newTabId = tabId || uuidModule.generate();
    const newTab: Tab = { id: newTabId, paneId, path, order, isDefault: isDefault || false };
    this.tabsMap.set(newTab.id, newTab);
    return newTab;
  }

  /**
   * Updates a tab
   * @param tabId - The id of the tab to update
   * @param updates - The updates to apply to the tab
   */
  _updateTab(tabId: string, updates: Partial<Tab>): void {
    const tab = this.tabsMap.get(tabId);
    if (tab) this.tabsMap.set(tabId, { ...tab, ...updates });
  }

  /**
   * Removes a tab
   * @param tabId - The id of the tab to remove
   */
  _deleteTab(tabId: string): void {
    this.tabsMap.delete(tabId);
  }

  /**
   * Navigates to a tab in the URL
   * @param tabId - The id of the tab to navigate to
   * @param path - The path to navigate to
   * @param config - Additional configuration options
   */
  _navigateToTab(
    tabId: string,
    path: NavigationPath,
    config?: NavigationConfig,
    options?: { disableTabChangeEvent?: boolean }
  ): void {
    const tab = this._getTab(tabId);
    if (!tab) return;

    const pane = this._getPane(tab.paneId);
    if (!pane) return;

    pane.activeTabId = tabId;
    this.activeTabId = tabId;
    this.setActivePane(tab.paneId);

    const finalPath = this._convertPathToToWithTid(path, tabId);
    const routerOptions = config?.routerOptions || {};
    if (routerOptions.state) this._updateTab(tabId, { state: routerOptions.state });

    // Notify listeners about tab change (non-new tab focus/navigation)
    if (options?.disableTabChangeEvent !== true) {
      this.onTabChange({
        tab,
        path,
        openInBackground: !!config?.openInBackground,
        isNew: false,
        suppressSidePanelRouteHandler: !!config?.suppressSidePanelRouteHandler,
      });
    }
    this.navigate?.(finalPath, routerOptions);
  }

  /**
   * Reorders tabs to have consecutive order values starting from 0
   * @param tabs - The tabs to reorder
   */
  _reorderTabs(tabs: Tab[]): void {
    tabs.forEach((tab, index) => {
      this._updateTab(tab.id, { order: index });
    });
  }

  /**
   * Reorders panes to have consecutive order values starting from 0
   */
  _reorderPanes(): void {
    const remainingPanes = [...this.panesMap.values()].sort((a, b) => a.order - b.order);
    remainingPanes.forEach((pane, index) => {
      this.panesMap.set(pane.id, { ...pane, order: index });
    });
  }

  /**
   * Reorders a tab within the same pane
   * @param tab - The tab to reorder
   * @param targetIndex - The index to move the tab to
   */
  _reorderTabsAfterInsert(tab: Tab, targetIndex: number): void {
    const currentPaneTabs = this.getTabsForPane(tab.paneId);
    const currentIndex = currentPaneTabs.findIndex(t => t.id === tab.id);

    // If moving to the same position, no need to do anything
    if (currentIndex === targetIndex) return;

    // Or else, create a new array with the tab moved to the target position
    const reorderedTabs = [...currentPaneTabs];
    const [movedTab] = reorderedTabs.splice(currentIndex, 1);
    reorderedTabs.splice(targetIndex, 0, movedTab);
    this._reorderTabs(reorderedTabs);
  }

  /**
   * Inserts a new pane
   * @param panes - The panes to insert the new pane into
   * @param afterPaneId - The pane to insert the new pane after
   * @returns The new pane, its id, and its order
   */
  _insertNewPane({ paneId, afterPaneId }: { paneId?: string; afterPaneId?: string }): Pane {
    const newPaneId = paneId || uuidModule.generate();
    let newPaneOrder: number;

    if (afterPaneId) {
      const afterPane = this.panesMap.get(afterPaneId);
      if (!afterPane) {
        // If the afterPaneId is not found, create a new pane at the end
        newPaneOrder = this._getNextPaneOrder(this.panes);
      } else {
        for (const pane of this.panes) {
          if (pane.order > afterPane.order) pane.order += 1;
        }
        newPaneOrder = afterPane.order + 1;
      }
    } else {
      newPaneOrder = this._getNextPaneOrder(this.panes);
    }

    const defaultTab: Tab = this._insertNewTab({
      paneId: newPaneId,
      path: this.NEW_TAB_PATH,
      order: 0,
      isDefault: true,
    });
    const newPane: Pane = { id: newPaneId, activeTabId: defaultTab.id, order: newPaneOrder };
    this.panesMap.set(newPaneId, newPane);
    return newPane;
  }

  /**
   * Deletes a pane
   * @param paneId - The id of the pane to delete
   */
  _deletePane(paneId: string): void {
    if (this.panes.length > 1) {
      this.panesMap.delete(paneId);
      this.activePaneId = this.lastActivePaneId || this.panes[0].id;
      const activePane = this.panesMap.get(this.activePaneId) || this.panes[0];
      this.activeTabId = activePane.activeTabId!;
      this.lastActivePaneId = null;
      this._reorderPanes();
    }
  }

  /**
   * Moves a tab to a different pane
   */
  _moveTabToDifferentPane(
    tab: Tab,
    targetPaneId: string,
    targetIndex: number,
    sourcePane: Pane,
    config?: NavigationConfig
  ): void {
    const sourcePaneTabs = this.getTabsForPane(tab.paneId);
    const targetPaneTabs = this.getTabsForPane(targetPaneId);

    // Check if the moved tab was the active one in its source pane
    const wasMovedTabActive = tab.id === sourcePane.activeTabId;

    // Create a new array for the target pane with the moved tab inserted
    const newTargetPaneTabs = [...targetPaneTabs];
    newTargetPaneTabs.splice(targetIndex, 0, { ...tab, paneId: targetPaneId });
    this._updateTab(tab.id, { paneId: targetPaneId });
    this._reorderTabs(newTargetPaneTabs);

    // Reorder remaining tabs in source pane to have consecutive order values
    const remainingSourceTabs = sourcePaneTabs.filter(t => t.id !== tab.id);
    if (remainingSourceTabs.length > 0) this._reorderTabs(remainingSourceTabs);

    if (remainingSourceTabs.length === 0) {
      this._deletePane(sourcePane.id);
      return;
    }

    // Only activate a new tab if the moved tab was the active one
    if (wasMovedTabActive) {
      // Find the tab closest to the moved tab's position
      const nextTab = this._calcNextTabToActivate(tab, remainingSourceTabs);

      this._navigateToTab(nextTab.id, nextTab.path, config);
    }
  }

  _calcNextTabToActivate(tab: Tab, remainingSourceTabs: Tab[]) {
    const movedTabOrder = tab.order;
    let nextTab = remainingSourceTabs[0]; // fallback to first tab

    if (remainingSourceTabs.length <= 1) {
      return nextTab;
    }

    // Find the tab with the closest order value to the moved tab
    // Prioritize tabs to the right when distances are equal
    let minDistance = Infinity;
    for (const candidateTab of remainingSourceTabs) {
      const distance = Math.abs(candidateTab.order - movedTabOrder);
      if (
        distance < minDistance ||
        (distance === minDistance && candidateTab.order > nextTab.order)
      ) {
        minDistance = distance;
        nextTab = candidateTab;
      }
    }

    return nextTab;
  }

  /**
   * Converts a path to To format with tid parameter
   */
  _convertPathToToWithTid(path: NavigationPath, tabId: string): To {
    let pathname: string;
    let search: string;

    if (typeof path === "string") {
      // Simple string path - parse existing search params if any
      const [pathnamePart, existingSearch] = path.split("?");
      pathname = pathnamePart;
      search = existingSearch || "";
    } else if ("path" in path) {
      // AppRoute - extract path and parse
      const [pathnamePart, existingSearch] = (path as AppRoute).path.split("?");
      pathname = pathnamePart;
      search = existingSearch || "";
    } else {
      // To object - normalize optional fields (be permissive on typing)
      const toObj = path as { pathname?: string; search?: string };
      pathname =
        typeof toObj.pathname === "string" && toObj.pathname.length > 0 ? toObj.pathname : "/";
      const rawSearch = toObj.search;
      search = typeof rawSearch === "string" ? rawSearch : "";
    }

    // Add tid parameter
    const searchParams = new URLSearchParams(search);
    searchParams.set("tid", tabId);
    search = searchParams.toString() ? `?${searchParams.toString()}` : "";

    return { pathname, search };
  }

  _getNextPaneOrder(panes: Pane[]): number {
    if (panes.length === 0) return 0;
    const maxOrder = Math.max(...panes.map(pane => pane.order));
    return maxOrder + 1;
  }

  _getNextTabOrder(tabs: Tab[]): number {
    if (tabs.length === 0) return 0;
    const maxOrder = Math.max(...tabs.map(tab => tab.order));
    return maxOrder + 1;
  }

  // ===== NAVIGATION METHODS =====

  /**
   * Consolidated function to open a tab using a specific strategy
   * @param path - The path to navigate to
   * @param strategy - The strategy to use for opening the tab
   * @param config - Additional configuration options
   */
  openTab({
    path,
    strategy,
    config,
  }: {
    path: NavigationPath;
    strategy: TabOpeningStrategy;
    config?: NavigationConfig & {
      activeTabId?: string;
      paneId?: string;
    };
  }) {
    switch (strategy) {
      case TabOpeningStrategy.SAME_TAB:
        this.openInSameTab({ path, config });
        break;
      case TabOpeningStrategy.NEW_TAB:
        this.openInNewTab({ path, config });
        break;
      case TabOpeningStrategy.INACTIVE_PANE:
        this.openInInactivePane({ path, config });
        break;
      case TabOpeningStrategy.NEW_TAB_BACKGROUND:
        this.openInNewTab({ path, config: { ...config, openInBackground: true } });
        break;
      case TabOpeningStrategy.INACTIVE_PANE_BACKGROUND:
        this.openInInactivePane({ path, config: { ...config, openInBackground: true } });
        break;
    }
  }

  /**
   * Opens a new tab in the specified pane or the active pane
   * @param path - The path to navigate to
   * @param config.activeTabId - Specifies the tab that the function is being called from
   */
  openInNewTab({
    path,
    config,
  }: {
    path: NavigationPath;
    config?: NavigationConfig & {
      activeTabId?: string;
      paneId?: string;
    };
  }) {
    const { activeTabId, paneId, openInBackground = false, ...restConfig } = config || {};

    // paneId takes precedence, then activeTabId, then current active pane
    let targetPaneId: string;
    if (paneId) {
      targetPaneId = paneId;
    } else if (activeTabId) {
      const activeTab = this.tabsMap.get(activeTabId);
      targetPaneId = activeTab?.paneId || this.activePaneId;
    } else {
      targetPaneId = this.activePaneId;
    }

    const pane = this._getPane(targetPaneId);
    if (!pane) return;

    const pathString = convertPathToStringWithoutTid(path);
    const paneTabs = this.getTabsForPane(targetPaneId);
    const existingTab = paneTabs.find(tab => tab.path === pathString);

    if (existingTab) {
      if (openInBackground) {
        // Bring the tab to front in its pane without stealing focus
        if (targetPaneId !== this.activePaneId) {
          this._setPaneActiveTabWithoutFocus(targetPaneId, existingTab.id);
        }

        // Notify listeners about tab change (existing tab, background)
        this.onTabChange({
          tab: existingTab,
          path,
          openInBackground: true,
          isNew: false,
          suppressSidePanelRouteHandler: !!config?.suppressSidePanelRouteHandler,
        });

        return;
      }
      this.switchToTab({ tabId: existingTab.id, config: restConfig });
    } else {
      const newTab = this._insertNewTab({
        paneId: targetPaneId,
        path: pathString,
        order: this._getNextTabOrder(paneTabs),
      });

      // Notify listeners about tab change (new tab)
      this.onTabChange({
        tab: newTab,
        path,
        openInBackground,
        isNew: true,
        suppressSidePanelRouteHandler: !!config?.suppressSidePanelRouteHandler,
      });

      trackEvent(TrackedEvent.TabOpen, {
        view_id: newTab.id,
        pane_index: pane.order,
      });

      if (openInBackground) {
        // Set as active tab for its pane without switching global focus
        if (targetPaneId !== this.activePaneId) {
          this._setPaneActiveTabWithoutFocus(targetPaneId, newTab.id);
        }

        return;
      }

      this._navigateToTab(newTab.id, path, restConfig, { disableTabChangeEvent: true });
    }
  }

  /**
   * Opens a new tab in the same pane as the active tab
   * @param path - The path to navigate to
   * @param config.activeTabId - Specifies the tab that the function is being called from
   */
  openInSameTab({
    path,
    config,
  }: {
    path: NavigationPath;
    config?: NavigationConfig & { activeTabId?: string };
  }) {
    const { activeTabId, ...restConfig } = config || {};

    // If activeTabId is provided but doesn't exist, fall back to current active tab
    let targetTabId = this.activeTabId!;
    if (activeTabId) {
      const specifiedTab = this._getTab(activeTabId);
      if (specifiedTab) targetTabId = activeTabId;
    }

    const tab = this._getTab(targetTabId);
    if (!tab) return;

    const pathString = convertPathToStringWithoutTid(path);
    const isSamePath = tab.path === pathString;
    if (!isSamePath) {
      this._updateTab(targetTabId, { path: pathString });
    }
    // Even if path is the same, still navigate if there is new state to apply
    if (isSamePath && !restConfig?.routerOptions?.state) return;

    if (
      !isSamePath &&
      // Don't overwrite the default tab.
      tab.isDefault &&
      // But don't create a new home tab either.
      (typeof path !== "object" || (path as Path).pathname !== appRoutes.notesList({}).path)
    ) {
      this.openInNewTab({ path, config: { ...restConfig, paneId: tab.paneId } });
      return;
    }

    this._navigateToTab(targetTabId, path, restConfig);
  }

  /**
   * Opens a new tab in the inactive pane (or opens a new pane if no inactive pane exists)
   * @param path - The path to navigate to
   * @param config.activeTabId - Specifies the tab that the function is being called from
   */
  openInInactivePane({
    path,
    config,
  }: {
    path: NavigationPath;
    config?: NavigationConfig & { activeTabId?: string };
  }) {
    const { activeTabId, openInBackground = false, ...restConfig } = config || {};

    // If the active tab is not the same as the active tab, set the active pane to the active tab's pane
    // This is because this function happens before the click is bubbled to the pane component
    if (activeTabId && activeTabId !== this.activeTabId) {
      const activeTab = this.tabsMap.get(activeTabId);
      if (activeTab) this.setActivePane(activeTab.paneId);
    }

    // If the last active pane exists, open a new tab in it
    if (this.lastActivePaneId) {
      const lastActivePane = this.panesMap.get(this.lastActivePaneId);

      if (lastActivePane) {
        // Check if there's an existing tab with the same path in the last active pane
        const pathString = convertPathToStringWithoutTid(path);
        const lastActivePaneTabs = this.getTabsForPane(this.lastActivePaneId);
        const existingTab = lastActivePaneTabs.find(tab => tab.path === pathString);

        if (existingTab) {
          if (openInBackground) {
            // Update inactive pane's active tab without focusing it
            if (this.lastActivePaneId !== this.activePaneId) {
              this._setPaneActiveTabWithoutFocus(this.lastActivePaneId, existingTab.id);
            }
            // Notify listeners about tab change (existing tab, background in inactive pane)
            this.onTabChange({
              tab: existingTab,
              path,
              openInBackground: true,
              isNew: false,
              suppressSidePanelRouteHandler: !!config?.suppressSidePanelRouteHandler,
            });
          } else {
            this.switchToTab({ tabId: existingTab.id, config: restConfig });
          }
        } else {
          this.openInNewTab({
            path,
            config: { ...restConfig, paneId: this.lastActivePaneId, openInBackground },
          });
        }

        return;
      }
    }

    // Otherwise, create a new pane with the path
    this.createPane({ path, config: { ...restConfig, openInBackground } });
  }

  /**
   * Creates a new pane
   * @param path - The path to navigate to; if not provided, a new tab will be created in the new pane
   * @param config - Additional configuration options
   * @param afterPaneId - The pane to insert the new pane after
   */
  createPane({
    path,
    config,
    afterPaneId,
  }: {
    path?: NavigationPath;
    afterPaneId?: string;
    config?: NavigationConfig;
  }) {
    const { openInBackground = false } = config || {};
    if (this.panesMap.size >= this.MAX_PANES) {
      logger.warn({
        message: `[NavigationTabManager][createPane] Maximum number of panes (${this.MAX_PANES}) reached`,
      });
      return;
    }

    trackEvent(TrackedEvent.PaneSplit, {
      origin_pane_index: afterPaneId ?? this.activePaneId,
    });

    const newPane = this._insertNewPane({ afterPaneId });
    if (openInBackground) {
      // Treat newly created pane as the inactive pane for subsequent background opens
      this.lastActivePaneId = newPane.id;
    }
    if (path) {
      const pathString = convertPathToStringWithoutTid(path);
      const newTab = this._insertNewTab({ paneId: newPane.id, path: pathString, order: 1 });

      // Notify listeners about tab change (new tab as part of new pane creation)
      this.onTabChange({
        tab: newTab,
        path,
        openInBackground,
        isNew: true,
        suppressSidePanelRouteHandler: !!config?.suppressSidePanelRouteHandler,
      });
      if (openInBackground) {
        // Make it the active tab in the new pane without focusing it
        this._setPaneActiveTabWithoutFocus(newPane.id, newTab.id);
        return;
      }
      this._navigateToTab(newTab.id, pathString, config, { disableTabChangeEvent: true });
    } else {
      if (openInBackground) {
        return;
      }
      this._navigateToTab(newPane.activeTabId!, this.NEW_TAB_PATH, config);
    }
  }

  /**
   * Sets a pane's active tab and triggers reactivity without navigating
   */
  _setPaneActiveTabWithoutFocus(paneId: string, tabId: string) {
    const pane = this._getPane(paneId);
    if (!pane) return;
    pane.activeTabId = tabId;
    this.panesMap.set(paneId, { ...pane });
  }

  /**
   * Splits a pane with a tab
   * If panes are maxed out, this will not create a new pane
   * @param tabId - The id of the tab to split into a new pane with
   * @param config - Additional configuration options
   */
  splitPaneWithTab({ tabId, config }: { tabId: string; config?: NavigationConfig }) {
    const tab = this._getTab(tabId);
    if (!tab) return;

    // Don't allow splitting the default tab
    if (tab.isDefault) {
      logger.warn({ message: `[NavigationTabManager][splitPaneWithTab] Cannot split default tab` });
      return;
    }

    const sourcePane = this._getPane(tab.paneId);
    if (!sourcePane) return;

    if (this.panesMap.size >= this.MAX_PANES) {
      logger.warn({
        message: `[NavigationTabManager][splitPaneWithTab] Maximum number of panes (${this.MAX_PANES}) reached`,
      });
      return;
    }

    // Create a new pane and move the tab to it
    const newPane = this._insertNewPane({});
    this._updateTab(tabId, { paneId: newPane.id, order: 0 });

    // Handle source pane's active tab if the moved tab was active
    if (sourcePane.activeTabId === tabId) {
      const sourcePaneTabs = this.getTabsForPane(sourcePane.id);
      if (sourcePaneTabs.length > 0) {
        const nextTab = sourcePaneTabs[0];
        sourcePane.activeTabId = nextTab.id;
      } else {
        this._deletePane(sourcePane.id);
      }
    }

    this._navigateToTab(tabId, tab.path, config);
  }

  /**
   * Moves a tab from one pane to another with a specified target index
   * @param tabId - The id of the tab to move
   * @param targetPaneId - The id of the pane to move the tab to
   * @param targetIndex - The target index in the target pane (0-based)
   * @param config - Additional configuration options
   */
  moveTabToPane({
    tabId,
    targetPaneId,
    targetIndex,
    config,
  }: {
    tabId: string;
    targetPaneId: string;
    targetIndex: number;
    config?: NavigationConfig;
  }) {
    const tab = this._getTab(tabId);
    if (!tab) return;

    const sourcePane = this._getPane(tab.paneId);
    const targetPane = this._getPane(targetPaneId);
    if (!sourcePane || !targetPane) return;

    // Don't allow moving the default tab from the default pane
    if (tab.isDefault) {
      logger.warn({
        message: `[NavigationTabManager][moveTabToPane] Cannot move default tab from default pane`,
      });
      return;
    }

    // Don't allow placing tabs before the default tab in the default pane
    if (targetIndex === 0) {
      logger.warn({
        message: `[NavigationTabManager][moveTabToPane] Cannot place tabs before the default tab`,
      });
      return;
    }

    // Validate target index
    const targetPaneTabs = this.getTabsForPane(targetPaneId);
    const maxIndex =
      tab.paneId === targetPaneId ? targetPaneTabs.length - 1 : targetPaneTabs.length;

    if (targetIndex < 0 || targetIndex > maxIndex) {
      logger.warn({
        message: `[NavigationTabManager][moveTabToPane] Invalid target index ${targetIndex} for pane ${targetPaneId} with ${targetPaneTabs.length} tabs`,
      });
      return;
    }

    const originalPath = tab.path;
    if (tab.paneId === targetPaneId) {
      this._reorderTabsAfterInsert(tab, targetIndex);
    } else {
      this._moveTabToDifferentPane(tab, targetPaneId, targetIndex, sourcePane, config);
    }

    this._navigateToTab(tabId, originalPath, config);
  }

  switchToTab({ tabId, config }: { tabId: string; config?: NavigationConfig }) {
    const tab = this._getTab(tabId);
    if (!tab) return;

    const pane = this._getPane(tab.paneId);
    if (!pane) return;

    trackEvent(TrackedEvent.TabView, {
      view_id: tabId,
      pane_index: pane.order,
    });

    this._navigateToTab(tabId, tab.path, config);
  }

  closeTab({ tabId, config }: { tabId: string; config?: NavigationConfig }) {
    const tab = this._getTab(tabId);
    if (!tab || tab.isDefault) return;

    const pane = this._getPane(tab.paneId);
    if (!pane) return;

    const paneTabs = this.getTabsForPane(tab.paneId);
    const currentIndex = paneTabs.findIndex(t => t.id === tabId);
    const remainingTabs = paneTabs.filter(t => t.id !== tabId);

    if (currentIndex === -1) return;

    if (remainingTabs.length > 0) {
      const targetIndex = currentIndex === 0 ? remainingTabs.length - 1 : currentIndex - 1;
      const targetTab = currentIndex === 0 ? remainingTabs[0] : paneTabs[targetIndex];
      if (tab.id === this.activeTabId) this._navigateToTab(targetTab.id, targetTab.path, config);
      else pane.activeTabId = targetTab.id;
    }

    this._deleteTab(tabId);

    // Reorder remaining tabs to have consecutive order values
    const updatedPaneTabs = this.getTabsForPane(tab.paneId);
    if (updatedPaneTabs.length > 0) this._reorderTabs(updatedPaneTabs);
    if (updatedPaneTabs.length === 0) this._deletePane(pane.id);
  }

  closePane({ paneId, config }: { paneId?: string; config?: NavigationConfig } = {}) {
    const targetPaneId = paneId || this.activePaneId;
    const targetPane = this._getPane(targetPaneId);
    if (!targetPane) return;

    if (this.panesMap.size > 1) {
      // If there are more than 1 pane, close the pane and remove all tabs from it
      this.tabsMap.forEach((tab, tabId) => {
        if (tab.paneId === targetPaneId) this._deleteTab(tabId);
      });
      this._deletePane(targetPaneId);
      const remainingPane = this.panes[0];
      this.switchToTab({ tabId: remainingPane.activeTabId!, config });
      this._makeSureActivePaneIsValid();
    } else {
      // If there is only 1 pane, close everything except for the default tab
      this.tabsMap.forEach((tab, tabId) => {
        if (tab.paneId === targetPaneId && !tab.isDefault) this._deleteTab(tabId);
      });
      const remainingTab = this.getTabsForPane(targetPaneId)[0];
      this.switchToTab({ tabId: remainingTab.id, config });
    }
  }

  _makeSureActivePaneIsValid({ config }: { config?: NavigationConfig } = {}) {
    const activePane = this.panesMap.get(this.activePaneId);
    if (activePane && activePane.activeTabId && this.tabsMap.get(activePane.activeTabId)) {
      return;
    }

    const firstValidPane = this.panes.find(
      pane => pane.activeTabId && this.tabsMap.get(pane.activeTabId)
    );
    if (firstValidPane) {
      this.switchToTab({ tabId: firstValidPane.activeTabId!, config });
      return;
    }

    for (const pane of this.panes) {
      const tabs = this.getTabsForPane(pane.id);
      if (tabs.length > 0) {
        this.switchToTab({ tabId: tabs[0].id, config });
        return;
      }
    }
  }

  clearTabState(tabId: string | null | undefined) {
    if (!tabId) return;
    const tab = this.tabsMap.get(tabId);
    if (tab?.state) this._updateTab(tabId, { state: undefined });
  }

  // ===== INITIALIZATION METHODS =====

  _initializeDefaultPane(): NavigationState {
    const tabsMap = new ObservableMap<string, Tab>();
    const panesMap = new ObservableMap<string, Pane>();

    const defaultPaneId = uuidModule.generate();
    const defaultTabId = uuidModule.generate();

    panesMap.set(defaultPaneId, {
      id: defaultPaneId,
      activeTabId: defaultTabId,
      order: 0,
    });

    tabsMap.set(defaultTabId, {
      id: defaultTabId,
      paneId: defaultPaneId,
      path: this.NEW_TAB_PATH,
      order: 0,
      isDefault: true,
    });

    return {
      panesMap,
      tabsMap,
      activePaneId: defaultPaneId,
      activeTabId: defaultTabId,
      lastActivePaneId: null,
    };
  }

  /**
   * Creates the initial state from the URL
   * @returns void
   */
  _createInitialStateFromUrl() {
    if (clientEnvModule.isDesktop()) return;

    const currentUrl = this._getCurrentUrl();
    if (!currentUrl) return;

    let targetPath = convertPathToString(currentUrl);
    if (!targetPath) targetPath = this.NEW_TAB_PATH;

    // Extract tid parameter from URL
    const searchString = typeof currentUrl.search === "string" ? currentUrl.search : "";
    const searchParams = new URLSearchParams(searchString);
    const tidParam = searchParams.get("tid");

    // If tid parameter exists, try to find the existing tab
    if (tidParam && this.tabsMap.has(tidParam)) {
      const existingTab = this.tabsMap.get(tidParam)!;
      const pane = this.panesMap.get(existingTab.paneId);
      if (pane) {
        this._navigateToTab(tidParam, targetPath);
        return;
      }
    }

    const defaultPane = this.panesMap.get(this.activePaneId)!;
    const cleanTargetPath = stripTidFromPath(targetPath);

    // Only create a new tab if the path is different from the default path
    if (cleanTargetPath !== this.NEW_TAB_PATH) {
      const newTabId = tidParam ? tidParam : uuidModule.generate();
      this._insertNewTab({
        tabId: newTabId,
        paneId: this.activePaneId,
        path: cleanTargetPath,
        order: this._getNextTabOrder(this.getTabsForPane(this.activePaneId)),
      });

      defaultPane.activeTabId = newTabId;
      this.activeTabId = newTabId;
      this._navigateToTab(newTabId, targetPath);
    }
  }

  handleUrlChange(location: { pathname: string; search: string }) {
    const searchParams = new URLSearchParams(location.search);
    const tidParam = searchParams.get("tid");

    if (tidParam) {
      const tab = this.tabsMap.get(tidParam);

      if (tab) {
        // Compare the paths (both without tid) to determine if update is needed
        const currentPath = location.pathname + (location.search || "");
        const cleanCurrentPath = stripTidFromPath(currentPath);
        if (tab.path !== cleanCurrentPath) {
          this._updateTab(tidParam, { path: cleanCurrentPath });
        }

        // Switch to the tab (no programmatic navigate here; browser already did)
        const pane = this.panesMap.get(tab.paneId);
        if (pane) {
          pane.activeTabId = tidParam;
          this.activeTabId = tidParam;
          this.setActivePane(tab.paneId);
        }
      } else {
        // Create new tab with the tid parameter
        const targetPath = location.pathname + (location.search || "") || this.NEW_TAB_PATH;
        const cleanPath = stripTidFromPath(targetPath);

        // Ensure we have a valid active pane
        if (!this.activePaneId || !this.panesMap.has(this.activePaneId)) {
          logger.warn({
            message:
              "[NavigationTabManager][handleUrlChange] No valid active pane found during URL change",
          });
          return;
        }

        const defaultPane = this.panesMap.get(this.activePaneId)!;

        this._insertNewTab({
          tabId: tidParam,
          paneId: this.activePaneId,
          path: cleanPath,
          order: this._getNextTabOrder(this.getTabsForPane(this.activePaneId)),
        });

        defaultPane.activeTabId = tidParam;
        this.activeTabId = tidParam;

        // Notify listeners about new tab created via URL navigation
        const createdTab = this.tabsMap.get(tidParam);
        if (createdTab) {
          this.onTabChange({
            tab: createdTab,
            path: targetPath,
            openInBackground: false,
            isNew: true,
            suppressSidePanelRouteHandler: false,
          });
        }
      }
    } else {
      // Prevent desktop from updating the path on launch
      if (clientEnvModule.isDesktop() && !this.hasDesktopLoaded) {
        this.desktopStartupUrlChangeCount++;
        return;
      }

      // No tid parameter - update the active tab's path
      const targetPath = location.pathname + (location.search || "") || this.NEW_TAB_PATH;
      const cleanPath = stripTidFromPath(targetPath);
      if (this.activeTabId) {
        const activeTab = this.tabsMap.get(this.activeTabId);
        if (activeTab) {
          this._updateTab(this.activeTabId, { path: cleanPath });
        }
      }
    }
  }

  /**
   * Gets the current URL
   * @returns The current URL, or null if it fails to get the URL
   */
  _getCurrentUrl(): To | null {
    try {
      const isDesktop = clientEnvModule.isDesktop();
      if (isDesktop) {
        const currentUrl = new URL(document.URL);
        const hash = currentUrl.hash;
        const pathname = hash.replace(/^#/, "") || "/";
        const search = currentUrl.search;
        return { pathname, search };
      } else {
        const currentUrl = new URL(document.URL);
        return {
          pathname: currentUrl.pathname,
          search: currentUrl.search,
        };
      }
    } catch (error) {
      logger.warn({
        message: "[NavigationTabManager][_getCurrentUrl] Failed to get current URL",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      return null;
    }
  }

  setNavigateFunction(navigate: NavigateFunction) {
    this.navigate = navigate;
  }

  setActivePane(paneId: string) {
    if (this.activePaneId !== paneId) {
      this.lastActivePaneId = this.activePaneId;
      this.activePaneId = paneId;
    }
  }

  setPaneFocused = throttle((paneId: string, config?: NavigationConfig) => {
    if (this.activePaneId === paneId) return;

    const pane = this.panesMap.get(paneId);
    if (!pane) return;
    const activeTab = this.tabsMap.get(pane.activeTabId!);
    if (!activeTab) return;

    this._navigateToTab(activeTab.id, activeTab.path, config);
  }, 500);

  // ===== SERIALIZATION METHODS =====

  _serializeState(): SerializedNavigationState {
    const panesRecord: Record<string, Pane> = {};
    const tabsRecord: Record<string, Tab> = {};

    this.panesMap.forEach((pane, key) => {
      panesRecord[key] = { ...pane };
    });

    this.tabsMap.forEach((tab, key) => {
      tabsRecord[key] = { ...tab };
    });

    return {
      panes: panesRecord,
      tabs: tabsRecord,
      activePaneId: this.activePaneId,
      activeTabId: this.activeTabId,
      lastActivePaneId: this.lastActivePaneId,
    };
  }

  _deserializeState(serializedState: SerializedNavigationState): SerializedNavigationState | null {
    try {
      if (
        !serializedState ||
        typeof serializedState !== "object" ||
        !serializedState.panes ||
        !serializedState.tabs ||
        typeof serializedState.activePaneId !== "string" ||
        typeof serializedState.activeTabId !== "string"
      ) {
        return null;
      }

      const panes: Record<string, Pane> = {};
      for (const [id, pane] of Object.entries(serializedState.panes)) {
        if (!pane || typeof pane !== "object") return null;
        panes[id] = { ...pane };
      }

      const tabs: Record<string, Tab> = {};
      for (const [id, tab] of Object.entries(serializedState.tabs)) {
        if (!tab || typeof tab !== "object") return null;
        tabs[id] = { ...tab };
      }

      return {
        panes,
        tabs,
        activePaneId: serializedState.activePaneId,
        activeTabId: serializedState.activeTabId,
        lastActivePaneId: serializedState.lastActivePaneId,
      };
    } catch (error) {
      logger.warn({
        message: "[NavigationTabManager][_deserializeState] Failed to deserialize state",
        info: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      return null;
    }
  }

  _saveToSessionStorage(): void {
    try {
      const serializedState = this._serializeState();
      const stateString = JSON.stringify(serializedState);
      const storage = clientEnvModule.isDesktop() ? localStorage : sessionStorage;
      storage.setItem(this.SESSION_STORAGE_KEY, stateString);
      logger.debug({
        message:
          "[NavigationTabManager][_saveToSessionStorage] Successfully saved state to storage",
        info: {
          panesCount: this.panesMap.size,
          tabsCount: this.tabsMap.size,
          stateSize: stateString.length,
        },
      });
    } catch (error) {
      logger.warn({
        message: "[NavigationTabManager][_saveToSessionStorage] Failed to save state to storage",
        info: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  _restoreFromSessionStorage(): NavigationState | null {
    try {
      const storage = clientEnvModule.isDesktop() ? localStorage : sessionStorage;
      const stateString = storage.getItem(this.SESSION_STORAGE_KEY);
      if (!stateString) {
        logger.debug({
          message:
            "[NavigationTabManager][_restoreFromSessionStorage] No saved state found in storage",
        });
        return null;
      }

      const serializedState: SerializedNavigationState = JSON.parse(stateString);
      const deserializedState = this._deserializeState(serializedState);

      if (deserializedState) {
        const panesMap = new ObservableMap(deserializedState.panes);
        const tabsMap = new ObservableMap(deserializedState.tabs);

        Object.entries(deserializedState.tabs).forEach(([key, tab]) => {
          tabsMap.set(key, { ...tab });
        });

        Object.entries(deserializedState.panes).forEach(([key, pane]) => {
          const restoredPane: Pane = {
            ...pane,
          };
          panesMap.set(key, restoredPane);
        });

        const state: NavigationState = {
          panesMap,
          tabsMap,
          activePaneId: deserializedState.activePaneId,
          activeTabId: deserializedState.activeTabId,
          lastActivePaneId: deserializedState.lastActivePaneId,
        };

        return this._validateNavigationState(state);
      } else {
        logger.warn({
          message:
            "[NavigationTabManager][_restoreFromSessionStorage] Failed to deserialize state from storage, falling back to default state",
        });
        return null;
      }
    } catch (error) {
      logger.warn({
        message:
          "[NavigationTabManager][_restoreFromSessionStorage] Failed to restore state from storage",
        info: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      return null;
    }
  }

  _validateNavigationState(state: NavigationState): NavigationState | null {
    logger.debug({
      message: "[NavigationTabManager][_validateNavigationState] Validating navigation state",
      info: { state: JSON.stringify(state) },
    });

    // If the state is invalid, we return a new empty state
    if (!state) return this._initializeDefaultPane();
    if (!state.panesMap) return this._initializeDefaultPane();
    if (!state.tabsMap) return this._initializeDefaultPane();
    if (!state.activePaneId) return this._initializeDefaultPane();
    if (!state.activeTabId) return this._initializeDefaultPane();

    // Validate active values
    // activePaneId and activeTabId must be valid
    if (!state.panesMap.has(state.activePaneId)) return this._initializeDefaultPane();
    if (!state.tabsMap.has(state.activeTabId)) return this._initializeDefaultPane();

    // Validate panes
    // - We must have at least one pane.
    // - The pane must have an activeTab that is a valid tab.
    // - The panes must have a valid order
    const panes = Array.from(state.panesMap.values());
    if (panes.length === 0) return this._initializeDefaultPane();
    const sortedPanes = panes.sort((a, b) => a.order - b.order);

    for (let i = 0; i < sortedPanes.length; i++) {
      const pane = sortedPanes[i];
      if (!pane.activeTabId) return this._initializeDefaultPane();
      if (!state.tabsMap.has(pane.activeTabId)) return this._initializeDefaultPane();
      if (pane.order !== i) return this._initializeDefaultPane();
    }

    // Build a map of tabs by pane
    const sortedTabsByPane: Record<string, Tab[]> = {};
    const tabs = Array.from(state.tabsMap.values());
    for (const tab of tabs) {
      if (!state.panesMap.has(tab.paneId)) return this._initializeDefaultPane();
      if (!sortedTabsByPane[tab.paneId]) sortedTabsByPane[tab.paneId] = [];
      sortedTabsByPane[tab.paneId].push(tab);
    }

    // Validate tabs by pane
    // - The first tab must be the home tab.
    // - The tabs must have a valid paneId and order.
    for (const pane of sortedPanes) {
      const tabs = sortedTabsByPane[pane.id];
      tabs.sort((a, b) => a.order - b.order);

      for (let i = 0; i < tabs.length; i++) {
        const tab = tabs[i];
        if (i === 0 && (!tab.isDefault || tab.path !== this.NEW_TAB_PATH)) {
          return this._initializeDefaultPane();
        }
        if (!tab.paneId) return this._initializeDefaultPane();
        if (tab.paneId !== pane.id) return this._initializeDefaultPane();
        if (tab.order !== i) return this._initializeDefaultPane();
      }
    }

    return state;
  }

  _clearSessionStorage(): void {
    try {
      const storage = clientEnvModule.isDesktop() ? localStorage : sessionStorage;
      storage.removeItem(this.SESSION_STORAGE_KEY);
      logger.debug({
        message: "[NavigationTabManager][_clearSessionStorage] Cleared state from storage",
      });
    } catch (error) {
      logger.warn({
        message: "[NavigationTabManager][_clearSessionStorage] Failed to clear state from storage",
        info: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
    }
  }

  clearPersistedState(): void {
    this._clearSessionStorage();
  }

  _attachPersistenceReaction() {
    reaction(
      () => ({
        panes: Array.from(this.panesMap.entries()),
        tabs: Array.from(this.tabsMap.entries()),
        activePaneId: this.activePaneId,
        activeTabId: this.activeTabId,
        lastActivePaneId: this.lastActivePaneId,
      }),
      () => {
        this._saveToSessionStorage();
      },
      { delay: 50 }
    );
  }
}
